import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { convertCurrency, formatCurrency } from '../../utils/currencyUtils';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, Label, Sector } from 'recharts';

// Увеличиваем размер контейнера для большей диаграммы
const ChartContainer = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 14px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.05),
              -4px -4px 8px rgba(255, 255, 255, 0.8);
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0 0 20px 0;
  text-align: center;
`;

// Увеличиваем контейнер для диаграммы
const ChartWrapper = styled.div`
  width: 100%;
  height: ${props => props.$isSmallScreen ? '350px' : '420px'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TotalAmount = styled.div`
  text-align: center;
  margin-top: 20px;
  padding: 12px;
  border-radius: 10px;
  background: linear-gradient(145deg, #f0f3f9, #e6ebf5);
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  
  .total-label {
    font-size: 14px;
    color: #6c7d95;
    margin-bottom: 5px;
  }
  
  .total-value {
    font-size: 22px;
    font-weight: 600;
    color: #4a6fa1;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 10px;
  min-width: 120px;
  
  .stat-label {
    font-size: 13px;
    color: #6c7d95;
    margin-bottom: 5px;
  }
  
  .stat-value {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.$color || '#4a6fa1'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #8c96a5;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 10px;
    fill: #d1d9e6;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #8c96a5;
  
  svg {
    width: 24px;
    height: 24px;
    margin-bottom: 8px;
    fill: #4a6fa1;
    animation: rotate 1.5s linear infinite;
  }
  
  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Иконки
const LoadingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8z"/>
  </svg>
);

const NoDataIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14h-2v-2h2v2zm0-4h-2V7h2v6zm4 4h-2v-4h2v4zm0-6h-2V7h2v4zm4 6h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);

// Измененные стили для детальной статистики - перемещены прямо под кнопку
const DetailedCategoryView = styled.div`
  margin-top: 10px;
  border-top: 1px solid #e6ebf5;
  padding-top: 15px;
  max-height: ${props => props.$isVisible ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.5s ease-in-out;
  opacity: ${props => props.$isVisible ? '1' : '0'};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transition: max-height 0.5s ease-in-out, opacity 0.3s ease-in-out;
`;

const CategoryDetails = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  border-left: 4px solid ${props => props.$color};
  background: ${props => props.$color ? props.$color + '15' : '#f7f9fc'};
  
  &:hover {
    background: ${props => props.$color ? props.$color + '25' : '#f0f3f9'};
  }
  
  .category-name {
    display: flex;
    align-items: center;
    font-weight: 600;
    
    .color-indicator {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background-color: ${props => props.$color};
      margin-right: 8px;
    }
    
    .expand-icon {
      margin-left: 5px;
      transition: transform 0.3s ease;
      transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0)'};
      font-size: 10px;
      color: #8c96a5;
    }
  }
  
  .category-stats {
    display: flex;
    align-items: center;
    
    .amount {
      font-weight: 500;
      margin-right: 8px;
      color: #4a6fa1;
    }
    
    .percent {
      font-size: 13px;
      color: #8c96a5;
    }
  }
`;

const SubcategoryList = styled.div`
  margin-top: 8px;
  margin-left: 20px;
  max-height: ${props => props.$isExpanded ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  opacity: ${props => props.$isExpanded ? '1' : '0'};
`;

const SubcategoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  margin-bottom: 5px;
  border-radius: 6px;
  border-left: 2px solid ${props => props.$color ? props.$color + '80' : '#e6ebf5'};
  background-color: ${props => props.$color ? props.$color + '08' : '#f9fafc'};
  
  &:hover {
    background-color: ${props => props.$color ? props.$color + '15' : '#f0f3f9'};
  }
  
  .subcategory-name {
    color: #333;
    font-size: 14px;
  }
  
  .subcategory-stats {
    display: flex;
    align-items: center;
    
    .amount {
      font-size: 14px;
      margin-right: 8px;
      color: #4a6fa1;
    }
    
    .percent {
      font-size: 12px;
      color: #8c96a5;
    }
  }
`;

// Изменение структуры - выделяем контейнер для детальных данных
const DetailedDataContainer = styled.div`
  margin-top: 20px;
`;

// Обновленный стиль для кнопки - теперь она отделена от детального представления
const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  background: linear-gradient(145deg, #f5f6fa, #eff1f5);
  border: none;
  border-radius: 8px;
  padding: 12px;
  margin-top: 15px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: #4a6fa1;
  text-align: center;
  justify-content: center;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(145deg, #eff1f5, #e6ebf5);
    color: #395982;
  }
  
  &:active {
    box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.05),
                inset -2px -2px 5px rgba(255, 255, 255, 0.8);
  }
  
  .icon {
    margin-right: 8px;
    transition: transform 0.3s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

// Цвета для категорий
const CATEGORY_COLORS = [
  '#4A6FA1', '#FF6B6B', '#4ECDC4', '#FFD166', '#7A77FF',
  '#F38181', '#3EC1D3', '#FFC75F', '#6C5CE7', '#FF9A76',
  '#A5DEE5', '#FDCB6E', '#E84393', '#B1A0FF', '#55E6C1'
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value, payload: { percentage, formattedAmount } } = payload[0];
    
    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: '12px', 
        border: '1px solid #ddd',
        borderRadius: '6px',
        boxShadow: '0px 2px 6px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold', color: '#4A6FA1' }}>{name}</p>
        <p style={{ margin: '5px 0 0', fontSize: '16px' }}>{formattedAmount}</p>
        <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#666' }}>
          {`${percentage.toFixed(1)}%`}
        </p>
      </div>
    );
  }

  return null;
};

const CustomLegend = ({ payload }) => {
  return (
    <ul style={{ 
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center'
    }}>
      {payload.slice(0, 5).map((entry, index) => (
        <li key={`legend-${index}`} style={{ 
          display: 'flex',
          alignItems: 'center',
          marginRight: '10px',
          marginBottom: '5px'
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            backgroundColor: entry.color,
            borderRadius: '50%',
            marginRight: '5px'
          }} />
          <span style={{ fontSize: '12px', color: '#6c7d95' }}>{entry.value}</span>
        </li>
      ))}
      {payload.length > 5 && (
        <li style={{ fontSize: '12px', color: '#6c7d95' }}>
          + {payload.length - 5} еще
        </li>
      )}
    </ul>
  );
};

// Компонент для отрисовки активного сектора (при выделении)
const renderActiveShape = (props) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle,
    fill, payload
  } = props;

  // Более тонкое и плавное выделение сектора
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 5} // Небольшое увеличение для выделения
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.9} // Делаем немного прозрачнее для более мягкого эффекта
        stroke={fill}
        strokeWidth={1}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={innerRadius - 3} // Внутреннее выделение
        outerRadius={innerRadius - 1}
        fill={fill}
      />
    </g>
  );
};

const CategoryChart = ({ 
  transactions, 
  exchangeRates, 
  preferredCurrency, 
  isLoading,
  enhancedView = false
}) => {
  const { t } = useTranslation();
  
  // Проверка размера экрана для адаптивной версии
  const isSmallScreen = window.innerWidth <= 768;
  
  // Состояние для отображения детальной статистики
  const [showDetailed, setShowDetailed] = useState(false);
  
  // Состояние для хранения раскрытых категорий
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Состояние для активного сектора
  const [activeIndex, setActiveIndex] = useState(null);
  
  // Обработчик переключения раскрытия категории
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Обработчик наведения на сектор
  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };
  
  // Обработчик ухода с сектора
  const onPieLeave = () => {
    setActiveIndex(null);
  };

  // Обработка данных для графика и детальной статистики
  const { 
    chartData, 
    detailedStats, 
    totalAmount, 
    averageAmount, 
    topCategory, 
    totalTransactions 
  } = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return { 
        chartData: [], 
        detailedStats: {},
        totalAmount: 0, 
        averageAmount: 0,
        topCategory: null,
        totalTransactions: 0
      };
    }

    // Структура для обработки данных
    const categories = {};
    const detailed = {};
    let total = 0;
    let countWithAmount = 0;

    transactions.forEach(transaction => {
      const amount = parseFloat(transaction.amount);
      if (isNaN(amount)) return;

      countWithAmount++;
      const originalCurrency = transaction.currency || 'UAH';
      // Конвертируем в предпочитаемую валюту
      const convertedAmount = convertCurrency(
        amount, 
        originalCurrency, 
        preferredCurrency, 
        exchangeRates
      );

      const category = transaction.category;
      const comment = (transaction.comment && transaction.comment.trim()) ? 
                    transaction.comment.trim() : t('no_comment');
      
      // Для графика
      if (!categories[category]) {
        categories[category] = 0;
      }
      categories[category] += convertedAmount;
      
      // Для детальной статистики
      if (!detailed[category]) {
        detailed[category] = {
          total: 0,
          subCategories: {}
        };
      }
      detailed[category].total += convertedAmount;
      
      if (!detailed[category].subCategories[comment]) {
        detailed[category].subCategories[comment] = {
          total: 0,
          count: 0
        };
      }
      detailed[category].subCategories[comment].total += convertedAmount;
      detailed[category].subCategories[comment].count += 1;
      
      total += convertedAmount;
    });

    // Преобразуем в массив для графика и вычисляем проценты
    const result = Object.entries(categories).map(([name, value]) => ({
      name,
      value,
      percentage: (value / total) * 100,
      formattedAmount: formatCurrency(value, preferredCurrency)
    }));

    // Сортируем по убыванию суммы
    const sortedResult = result.sort((a, b) => b.value - a.value);
    
    // Вычисляем проценты для детальной статистики
    Object.entries(detailed).forEach(([category, data]) => {
      data.percentage = (data.total / total * 100);
      
      Object.entries(data.subCategories).forEach(([subCategory, subData]) => {
        subData.percentage = (subData.total / data.total * 100);
      });
    });
    
    // Находим топовую категорию
    const topCat = sortedResult.length > 0 ? sortedResult[0] : null;
    
    // Средняя сумма транзакции
    const avgAmount = countWithAmount > 0 ? total / countWithAmount : 0;

    return {
      chartData: sortedResult,
      detailedStats: detailed,
      totalAmount: total,
      averageAmount: avgAmount,
      topCategory: topCat,
      totalTransactions: transactions.length
    };
  }, [transactions, exchangeRates, preferredCurrency, t]);

  const formattedTotalAmount = useMemo(() => {
    return formatCurrency(totalAmount, preferredCurrency);
  }, [totalAmount, preferredCurrency]);

  const formattedAverageAmount = useMemo(() => {
    return formatCurrency(averageAmount, preferredCurrency);
  }, [averageAmount, preferredCurrency]);

  if (isLoading) {
    return (
      <ChartContainer>
        <ChartTitle>{t('category_distribution_title')}</ChartTitle>
        <LoadingState>
          <LoadingIcon />
          <div>{t('chart_loading')}</div>
        </LoadingState>
      </ChartContainer>
    );
  }

  if (chartData.length === 0) {
    return (
      <ChartContainer>
        <ChartTitle>{t('category_distribution_title')}</ChartTitle>
        <EmptyState>
          <NoDataIcon />
          <div>{t('no_chart_data')}</div>
        </EmptyState>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ChartTitle>{t('category_distribution_title')}</ChartTitle>
      
      {/* Увеличенная диаграмма занимает всю ширину */}
      <ChartWrapper $isSmallScreen={isSmallScreen}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={isSmallScreen ? 110 : 140}
              innerRadius={isSmallScreen ? 55 : 70}
              fill="#8884d8"
              dataKey="value"
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                if (percent < 0.05) return null; // Не показываем маленькие сегменты
                
                const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
                
                return (
                  <text 
                    x={x} 
                    y={y} 
                    fill="white" 
                    textAnchor="middle" 
                    dominantBaseline="middle"
                    fontSize={isSmallScreen ? 12 : 14}
                    fontWeight="bold"
                  >
                    {`${(percent * 100).toFixed(0)}%`}
                  </text>
                );
              }}
              onClick={(entry, index) => {
                // При клике на сегмент раскрываем соответствующую категорию
                toggleCategory(entry.name);
                setShowDetailed(true); // Автоматически показываем детали при клике на сегмент
                // Выделяем активный сектор
                setActiveIndex(index);
              }}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} 
                />
              ))}
              <Label 
                value={t('pie_chart_center')}
                position="center"
                fontSize={isSmallScreen ? 12 : 14}
                fill="#6c7d95"
              />
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </PieChart>
        </ResponsiveContainer>
      </ChartWrapper>
      
      {/* Кнопка переключения для детальной статистики - перемещена сразу под диаграмму */}
      <ToggleButton 
        onClick={() => setShowDetailed(!showDetailed)} 
        $isOpen={showDetailed}
      >
        <span className="icon">▼</span>
        {showDetailed ? t('hide_detailed_stats') : t('show_detailed_stats')}
      </ToggleButton>
      
      {/* Детальная статистика - показывается сразу под кнопкой */}
      <DetailedCategoryView $isVisible={showDetailed}>
        {chartData.map((category, index) => {
          const categoryName = category.name;
          const categoryData = detailedStats[categoryName];
          const isExpanded = !!expandedCategories[categoryName];
          const categoryColor = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
          
          return (
            <CategoryDetails key={categoryName}>
              <CategoryHeader 
                onClick={() => toggleCategory(categoryName)}
                $isExpanded={isExpanded}
                $color={categoryColor}
              >
                <div className="category-name">
                  <div className="color-indicator"></div>
                  {categoryName}
                  <span className="expand-icon">▼</span>
                </div>
                <div className="category-stats">
                  <span className="amount">
                    {formatCurrency(categoryData.total, preferredCurrency)}
                  </span>
                  <span className="percent">
                    {categoryData.percentage.toFixed(1)}%
                  </span>
                </div>
              </CategoryHeader>
              
              <SubcategoryList $isExpanded={isExpanded}>
                {Object.entries(categoryData.subCategories)
                  .sort(([, a], [, b]) => b.total - a.total)
                  .map(([subCategory, subData]) => (
                    <SubcategoryItem 
                      key={`${categoryName}-${subCategory}`}
                      $color={categoryColor}
                    >
                      <div className="subcategory-name">
                        {subCategory} ({subData.count})
                      </div>
                      <div className="subcategory-stats">
                        <span className="amount">
                          {formatCurrency(subData.total, preferredCurrency)}
                        </span>
                        <span className="percent">
                          {subData.percentage.toFixed(1)}%
                        </span>
                      </div>
                    </SubcategoryItem>
                  ))}
              </SubcategoryList>
            </CategoryDetails>
          );
        })}
      </DetailedCategoryView>
      
      {/* Дополнительная статистика (опционально) - перемещена ниже детальной статистики */}
      {enhancedView && (
        <StatsContainer>
          <StatItem>
            <div className="stat-label">{t('total_trans_count')}</div>
            <div className="stat-value">{totalTransactions}</div>
          </StatItem>
          
          <StatItem>
            <div className="stat-label">{t('avg_transaction')}</div>
            <div className="stat-value">{formattedAverageAmount}</div>
          </StatItem>
          
          {topCategory && (
            <StatItem $color={CATEGORY_COLORS[0]}>
              <div className="stat-label">{t('top_category_stat')}</div>
              <div className="stat-value">{topCategory.name}</div>
            </StatItem>
          )}
        </StatsContainer>
      )}
      
      {/* Общая сумма - всегда отображается внизу */}
      <TotalAmount>
        <div className="total-label">{t('total_amount')}</div>
        <div className="total-value">{formattedTotalAmount}</div>
      </TotalAmount>
    </ChartContainer>
  );
};

export default CategoryChart;